import React, { useContext, useState, useEffect, useCallback } from "react";
import { Switch, Route, useHistory, useParams } from "react-router-dom";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";

// @ts-ignore
import { store } from "react-notifications-component";

import GlobalData from "../../context/globaldata";

import RateRequestEdit from "./Edit";
import RateRequestSelect from "./Select";

import Button from "../../components/Button/Button";
import { Header, Navigation } from "../../components/Layout";
import Modal from "../../components/Modal/Modal";

import { ReactComponent as IconArrowRight } from "../../assets/images/icons/arrow-right.svg";
import { nanoid } from "nanoid";

export default function RateRequestEditIndex(props: any) {
  process.env.NODE_ENV === "development" &&
    console.log("Re-rendering RateRequestEditIndex.tsx");

  const history = useHistory();
  const { uid } = useParams() as any;
  const context = useContext(GlobalData);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [showError, setShowError] = useState(false);
  const [hasErrors, setHasErrors] = useState(true);
  const [requestRatesModalVisible, setRequestRatesModalVisible] =
    useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState<boolean>(false);
  const [selectedInfoIndex, setSelectedInfoIndex] = useState<number>(0);
  const [currentStep, setCurrentStep] = useState<number>(1);

  const labels = context.rateRequestLabels;

  const openInfoModal = (rateRequestMode: number) => {
    setSelectedInfoIndex(rateRequestMode || 0);
    setInfoModalOpen(true);
  };

  const gotoNextStep = () => {
    setShowError(true);
    setHasErrors(checkErrors(true, true));

    if (!hasErrors) {
      history.push(navigationItems[currentStep].to);
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const checkErrors = useCallback(
    (withFocus = false, showErrorOverride = false) => {
      // @ts-ignore
      const invalidElement: HTMLElement = document.querySelector(
        ".is-invalid input, .quote-options-list:empty + .button",
      );

      if (invalidElement) {
        if ((showErrorOverride || showError) && withFocus) {
          invalidElement.focus();
        }

        return true;
      }

      return false;
    },
    [showError],
  );

  useEffect(() => {
    setTimeout(() => {
      setHasErrors(checkErrors());
    }, 1);
  }, [props.rateRequest, showError, checkErrors]);

  const hasSuppliers = () => {
    return (
      props.rateRequest.selectedSuppliers &&
      props.rateRequest.selectedSuppliers.length > 0
    );
  };

  let navigationItems = [] as any;

  const saveChangesAwait = async () => {
    process.env.NODE_ENV === "development" &&
      console.info("💾 Saving changes and awaiting");
    await saveChanges(false, null, null, false);
  };

  if (
    !props.rateRequest.group?.prevRateRequestRef &&
    !props.rateRequest.group?.nextRateRequestRef
  ) {
    navigationItems = [
      {
        to: `/rate-requests/${uid}/edit`,
        label: "Create rate request",
        exact: true,
        step: 1,
      },
      {
        to: `/rate-requests/${uid}/select`,
        label: "Select suppliers",
        exact: true,
        step: 2,
        disabled: hasErrors,
      },
    ];
  }

  if (props.rateRequest.group?.documents) {
    navigationItems = [
      {
        to: `/rate-requests/${props.rateRequest.group.documents[0].id}/edit`,
        label: "Origin",
        exact: true,
        step: 1,
      },
      {
        to: `/rate-requests/${props.rateRequest.group.documents[0].id}/select`,
        label: "Origin suppliers",
        exact: true,
        step: 2,
      },
      {
        to: `/rate-requests/${props.rateRequest.group.documents[1].id}/edit`,
        label: "Destination",
        exact: true,
        step: 3,
      },
      {
        to: `/rate-requests/${props.rateRequest.group.documents[1].id}/select`,
        label: "Destination suppliers",
        exact: true,
        step: 4,
      },
    ];
  }

  const saveChanges = async (
    exit: boolean = false,
    newData?: RateRequest | null,
    exitLocation: string | null = null,
    notify = true,
  ) => {
    props.setPending(true);
    const newRateRequestData = newData || props.rateRequest;

    process.env.NODE_ENV === "development" &&
      console.info("💾 Saving newRateRequestData", newRateRequestData);

    await context.firestore
      .collection("rate_requests")
      .doc(uid)
      .set(newRateRequestData)
      .then(async () => {
        if (notify) {
          store.addNotification({
            message: "Rate request successfully saved",
            type: "success",
            insert: "top",
            container: "top-center",
            animationIn: ["animated", "slideDown"],
            animationOut: ["animated", "slideUp"],
            dismiss: {
              pauseOnHover: true,
              duration: 5000,
            },
          });
        }

        // Update next rate request if it exists
        const nextRateRequestId =
          newRateRequestData.group?.nextRateRequestRef?.id;

        if (nextRateRequestId) {
          process.env.NODE_ENV === "development" &&
            console.info("💾 Updating next rate request", nextRateRequestId);

          await context.firestore
            .collection("rate_requests")
            .doc(nextRateRequestId)
            .update({
              freightType: newRateRequestData.freightType,
              origin: newRateRequestData.destination,
              originAddress: newRateRequestData.destinationAddress,
              quoteOptions: newRateRequestData.quoteOptions,
            });
        }

        props.setPending(false);

        exit && history.push(exitLocation || "/rate-requests");
      })
      .catch((error: string) => {
        store.addNotification({
          message: `Error saving rate request: ${error}`,
          type: "danger",
          insert: "top",
          container: "top-center",
          animationIn: ["animated", "slideDown"],
          animationOut: ["animated", "slideUp"],
          dismiss: {
            pauseOnHover: true,
            duration: 5000,
          },
        });

        props.setPending(false);
      });
  };

  const deleteRateRequest = () => {
    props.setDeleted(true);
    context.firestore
      .collection("rate_requests")
      .doc(uid)
      .delete()
      .then(() => {
        store.addNotification({
          message: `Successfully deleted rate request: ${uid}`,
          type: "success",
          insert: "top",
          container: "top-center",
          animationIn: ["animated", "slideDown"],
          animationOut: ["animated", "slideUp"],
          dismiss: {
            pauseOnHover: true,
            duration: 5000,
          },
        });

        history.push("/rate-requests");
      })
      .catch((error: string) => {
        props.setDeleted(false);
        store.addNotification({
          message: `Error deleting rate request: ${error}`,
          type: "danger",
          insert: "top",
          container: "top-center",
          animationIn: ["animated", "slideDown"],
          animationOut: ["animated", "slideUp"],
          dismiss: {
            pauseOnHover: true,
            duration: 5000,
          },
        });
      });
  };

  // Disabled for now, feature is unfinished
  // Should request rates from all siblings (check RequestModal.tsx for associated code)
  // async function requestAllRates() {
  //   const firstRateRequest = props.rateRequest.group?.documents[0] || props.rateRequest;

  //   // Check if rate request has siblings
  //   if (props.rateRequest.group?.documents) {
  //     // If so, request rates for all siblings
  //     await props.rateRequest.group.documents.forEach(async (rateRequest: RateRequest) => {
  //       await requestRates(rateRequest);
  //     });
  //   } else {
  //     // If not, request rates for current rate request
  //     await requestRates(props.rateRequest);
  //   }

  //   history.push(`/rate-requests/${props.rateRequest.uid}/quotes`);
  // }

  async function requestRates() {
    props.setPending(true);
    // Create responses subcollection with pre-determined response objects for each supplier

    // Set up quoteOptionsResponse array based on supplied quoteOptions in rateRequest
    const quoteOptionResponses: Array<QuoteOptionResponse> = [];

    props.rateRequest.quoteOptions.forEach((quoteOption: QuoteOption) => {
      const quoteOptionResponse: QuoteOptionResponse = {
        id: "", // Set id for each supplier uniquely
        quoteOptionId: quoteOption.id,
        accepted: true,
        rate: null,
        loadingDate: null,
        deliveryDate: null,
        status: 0,
        message: null,
        selectedStatus: {
          label: "Accept bid",
          value: true,
        },
      };

      quoteOptionResponses.push(quoteOptionResponse);
    });

    // Set up additionalsResponses array based on supplied additionals in rateRequest
    const additionalsResponses: Array<AdditionalResponse> = [];

    props.rateRequest.additionals.forEach(
      (additionalSelection: RateRequestAdditionalSelection) => {
        if (
          additionalSelection.selectedOrigin ||
          additionalSelection.selectedDestination
        ) {
          const additionalResponse: AdditionalResponse = {
            additionalId: additionalSelection.id,
            rateOrigin: null,
            rateDestination: null,
          };

          additionalsResponses.push(additionalResponse);
        }
      },
    );

    // Set up customAdditionalsResponses array based on supplied additionals in rateRequest
    const customAdditionalsResponses: Array<AdditionalResponse> = [];

    props.rateRequest.customAdditionals.forEach(
      (additionalSelection: RateRequestAdditionalSelection) => {
        if (
          additionalSelection.selectedOrigin ||
          additionalSelection.selectedDestination
        ) {
          const additionalResponse: AdditionalResponse = {
            additionalId: additionalSelection.id,
            rateOrigin: null,
            rateDestination: null,
          };

          customAdditionalsResponses.push(additionalResponse);
        }
      },
    );

    // Start batch
    const batch = context.firestore.batch();

    const selectedUids: Array<string> = [];

    // Create response document for each selected supplier
    props.rateRequest.selectedSuppliers
      .filter((selectedSupplier: SelectedSupplier) => {
        // Filtering the suppliers on duplicates.
        // Should not be necessary but there is a rare bug where suppliers apparently get selected twice

        if (selectedUids.includes(selectedSupplier.supplierUid)) {
          return false;
        }

        selectedUids.push(selectedSupplier.supplierUid);

        return true;
      })
      .forEach((selectedSupplier: SelectedSupplier) => {
        const quoteOptionResponsesWithIds = quoteOptionResponses.map(
          (quoteOptionResponse: QuoteOptionResponse) => {
            quoteOptionResponse.id = nanoid();

            return quoteOptionResponse;
          },
        );

        const response: RateRequestResponse = {
          supplierUid: selectedSupplier.supplierUid,
          // supplierEmail: selectedSupplier.supplierUid,
          status: 0,
          contactPerson: null,
          referenceInput: null,
          notes: [],
          additionalsResponses: additionalsResponses,
          customAdditionalsResponses: customAdditionalsResponses,
          quoteOptionsResponses: quoteOptionResponsesWithIds,
          submittedAt: null,
          overrideSupplierEmail: selectedSupplier.overrideEmail || "",
        };

        const newDocRef = context.firestore
          .collection("rate_requests")
          .doc(uid)
          .collection("responses")
          .doc();

        batch.set(newDocRef, response);
      });

    // Commit batch
    await batch.commit().then(() => {
      if (props.rateRequest.group?.documents.length > 1) {
        if (props.rateRequest.group?.index === 0) {
          // First rate request in group
          // Make sure to navigate to next rate request in group
          saveChanges(
            true,
            {
              ...props.rateRequest,
              status: 1,
              userSubmitted: context.currentUser.custom?.uid,
            },
            `/rate-requests/${props.rateRequest.group.documents[1].id}/edit`,
            false,
          );
          setCurrentStep(3);
          setRequestRatesModalVisible(false);
        } else if (props.rateRequest.group?.index === 1) {
          // Second rate request in group
          // Make sure to navigate to previous rate request in group
          saveChanges(
            true,
            {
              ...props.rateRequest,
              status: 1,
              userSubmitted: context.currentUser.custom?.uid,
            },
            `/rate-requests`,
          );
        }
      } else {
        // Single rate request
        saveChanges(
          true,
          {
            ...props.rateRequest,
            status: 1,
            userSubmitted: context.currentUser.custom?.uid,
          },
          `/rate-requests/${props.rateRequest.uid}/quotes`,
        );
      }
    });
  }

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      console.log(currentStep);
    }
  }, [currentStep]);

  return (
    <>
      <Header
        leftContent={
          <Button variant="ghost" onClick={() => saveChanges(true)}>
            Save and exit
          </Button>
        }
        middleContent={
          <Navigation
            navItems={navigationItems}
            beforeNavCallback={saveChangesAwait}
            variant="secondary"
            setCurrentStep={setCurrentStep}
          />
        }
        rightContent={
          <>
            {props.rateRequest.group?.documents ? (
              <>
                {(currentStep == 1 || currentStep == 3) && (
                  <Button
                    onClick={async () => {
                      await saveChangesAwait();
                      gotoNextStep();
                    }}
                    disabled={props.pending}
                  >
                    <>
                      <span>Next step</span>
                      <IconArrowRight className="button__icon" />
                    </>
                  </Button>
                )}

                {(currentStep === 2 || currentStep === 4) && (
                  <Button
                    onClick={async () => {
                      await saveChangesAwait();
                      setRequestRatesModalVisible(true);
                    }}
                    disabled={!hasSuppliers()}
                  >
                    <>
                      <span>Request rates</span>
                      <IconArrowRight className="button__icon" />
                    </>
                  </Button>
                )}
              </>
            ) : (
              <>
                {currentStep < 2 && (
                  <Button onClick={gotoNextStep} disabled={props.pending}>
                    <>
                      <span>Next step</span>
                      <IconArrowRight className="button__icon" />
                    </>
                  </Button>
                )}

                {currentStep === 2 && (
                  <Button
                    onClick={async () => {
                      setRequestRatesModalVisible(true);
                    }}
                    disabled={!hasSuppliers()}
                  >
                    <>
                      <span>Request rates</span>
                      <IconArrowRight className="button__icon" />
                    </>
                  </Button>
                )}
              </>
            )}
          </>
        }
      />

      <form onSubmit={() => saveChanges}>
        <Switch>
          <Route
            exact
            path="/rate-requests/:uid/edit"
            render={() => (
              <RateRequestEdit
                rateRequest={props.rateRequest}
                openInfoModal={openInfoModal}
                setRateRequest={props.setRateRequest}
                openDeleteModal={() => setDeleteModalVisible(true)}
                pending={props.pending}
                showError={showError}
              />
            )}
          />
          <Route
            exact
            path="/rate-requests/:uid/select"
            render={() => (
              <RateRequestSelect
                rateRequest={props.rateRequest}
                setRateRequest={props.setRateRequest}
                saveChanges={saveChanges}
                pending={props.pending}
                requestRatesModalVisible={requestRatesModalVisible}
                setRequestRatesModalVisible={setRequestRatesModalVisible}
                requestRates={requestRates}
              />
            )}
          />
        </Switch>
      </form>

      <Modal
        variant="danger"
        title="Are you sure?"
        visible={deleteModalVisible}
        pending={props.pending}
        confirmAction={deleteRateRequest}
        confirmLabel="Delete request"
        cancelAction={() => setDeleteModalVisible(false)}
      >
        Deleting this rate request draft makes it no longer accessible.
      </Modal>

      <Modal
        visible={infoModalOpen}
        title={"Request modes"}
        noCancelButton={true}
        cancelAction={() => setInfoModalOpen(false)}
        size="large"
      >
        <Tabs className="tab-container" defaultIndex={selectedInfoIndex}>
          <TabList>
            {labels.rateRequestModes[props.rateRequest.freightType || 0].map(
              (rateRequestMode: any, index: number) => (
                <Tab
                  key={rateRequestMode.labelShort + index}
                  onClick={() => setSelectedInfoIndex(rateRequestMode.value)}
                >
                  {rateRequestMode.labelShort}
                </Tab>
              ),
            )}
          </TabList>

          <div className="tab-content">
            {/* General */}
            {labels.rateRequestModes[props.rateRequest.freightType || 0].map(
              (rateRequestMode: any, index: number) => (
                <TabPanel key={rateRequestMode.labelShort + index}>
                  <h5>
                    <b>
                      {rateRequestMode.labelLong} ({rateRequestMode.labelShort})
                    </b>
                  </h5>
                  {rateRequestMode.info}
                </TabPanel>
              ),
            )}
          </div>
        </Tabs>
      </Modal>
    </>
  );
}
